// React & Hooks
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Tabulator } from 'react-tabulator/lib/types/TabulatorTypes';
import arrowLeft from '@/assets/icon/Arrow1.png';
import arrowRight from '@/assets/icon/Arrow2.png';
import { useAuthorityManagement } from '@/domains/system-management/hooks/useAuthorityManagement';
// Custom hooks
import { useGroupManagement } from '@/domains/system-management/hooks/useGroupManagement';
import type {
  ButtonState,
  Program,
  ProgramParams,
} from '@/domains/system-management/types/authority';
// Types
import type { GroupParams } from '@/domains/system-management/types/group';
// UI Components
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import Button from '@/shared/components/ui/Button';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import {
  type ColumnDefinition,
  ReactTabulator,
} from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import Select from '@/shared/components/ui/Select/Select';
import useCommonCodes from '@/shared/hooks/useCommonCodes';

const AuthorityManagement = () => {
  const { t } = useTranslation();
  const totalText = t('cmn > total');
  const resultText = t('cmn > results');

  // tabulator top
  const commonColumnProps = {
    hozAlign: 'center' as const,
    headerHozAlign: 'center' as const,
    resizable: false,
  };

  const columnInit = [
    {
      title: t('cmn > no'),
      formatter: (cell: Tabulator.CellComponent) => {
        const row = cell.getRow();
        const page = row.getTable().getPage();
        const pageSize = row.getTable().getPageSize();
        return (Number(page) - 1) * Number(pageSize) + row.getPosition(true);
      },
      width: 65,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('authority > name'),
      field: 'group_name',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('authority > id'),
      field: 'group_code',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('cmn > team'),
      field: 'description',
      widthGrow: 2,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('002'),
      field: 'usage',
      widthGrow: 2,
      headerSort: false,
      ...commonColumnProps,
    },
  ];

  const columnAssign = [
    {
      formatter: 'rowSelection',
      titleFormatter: 'rowSelection',
      width: 60,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('program > upper program'),
      field: 'upper_program',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('program > id'),
      field: 'lower_program',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: 'id',
      field: 'id',
      width: '100',
      visible: false,
      ...commonColumnProps,
    },
    {
      title: t('program > program name'),
      field: 'program_name',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnProps,
    },
  ];

  const columnStandby = [
    {
      formatter: 'rowSelection',
      titleFormatter: 'rowSelection',
      width: 60,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('program > upper program'),
      field: 'upper_program',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('program > id'),
      field: 'lower_program',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: 'id',
      field: 'id',
      width: '100',
      visible: false,
      ...commonColumnProps,
    },
    {
      title: t('program > program name'),
      field: 'program_name',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnProps,
    },
  ];

  // refs
  const tbRefInit = useRef<Tabulator | null>(null);
  const tbRefStandby = useRef<Tabulator | null>(null);
  const tbRefAssign = useRef<Tabulator | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // states
  const [groupName, setGroupName] = useState('');
  const [hasChanged, setHasChanged] = useState(false);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [programsAssign, setProgramsAssign] = useState<Program[]>([]);
  const [selectedRowAssign, setSelectedRowAssign] = useState<Program[]>([]);
  const [selectedRowStandby, setSelectedRowStandby] = useState<Program[]>([]);
  const [selectedIsCodeGroup, setSelectedIsCodeGroup] = useState<string>('');
  const [selectedUpperProgram, setSelectedUpperProgram] = useState<string>('');
  const [initialProgramsAssign, setInitialProgramsAssign] = useState<Program[]>([]);
  const [isRowClicked, setIsRowClicked] = useState<boolean>(false);
  const [optionParams] = useState('upper-code=002');
  const [selectedCode, setSelectedCode] = useState<{
    group_code: number | null;
  }>({
    group_code: null,
  });

  const hasChangedRef = useRef(hasChanged);
  const rowClickedRef = useRef(isRowClicked);

  //Button
  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    restore: true,
    arrow: true,
  });

  useEffect(() => {
    hasChangedRef.current = hasChanged;
    rowClickedRef.current = isRowClicked;
  }, [hasChanged, isRowClicked]);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  const [queryParamsProgram, setQueryParamsProgram] = useState<ProgramParams>({
    usage: '002001',
    deletion: '001002',
  });

  const [queryParams, setQueryParams] = useState<GroupParams>({});
  const { useGroups } = useGroupManagement(queryParams);
  const { data: groupsData = [] } = useGroups();

  const { useProgramAuthenticated, useUpperProgram, useProgramGroup, useCreateGroupAuthority } =
    useAuthorityManagement(queryParamsProgram);

  const { data: programsData = [] } = useProgramAuthenticated();
  const { data: upperProgramsData = [] } = useUpperProgram();
  const { data: programGroupsData } = useProgramGroup(selectedCode?.group_code ?? 0);
  const createGroupMutation = useCreateGroupAuthority(() => {
    setHasChanged(false);
    setButtonState((prevState) => ({
      ...prevState,
      confirm: true,
      restore: true,
    }));
  });

  const { commonListData } = useCommonCodes({ optionParams });
  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'), //text for the first page button
        first_title: t('cmn > first page'), //tooltip text for the first page button
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const commonTableOptions = {
    debugInvalidOptions: true,
    movableRows: false,
    resizableRows: false,
    height: '270px',
    // layout: 'fitColumns',
    rowHeight: 41,
    selectableRows: true,
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
  };

  const optionsInit = {
    debugInvalidOptions: true,
    pagination: true,
    movableRows: false,
    resizableRows: false,
    paginationSize: 5,
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    rowHeight: 41,
    selectableRows: 1,
    selectableRowsCheck: (row: Tabulator.RowComponent) => {
      return !row.getElement().classList.contains('tabulator-selected');
    },
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${totalText} ${
      groupsData?.length || 0
    } ${resultText}</div>`,
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
  };

  const optionsAssign = {
    ...commonTableOptions,
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${totalText} ${
      programsAssign?.length || 0
    } ${resultText}</div>`,
  };
  const optionsStandby = {
    ...commonTableOptions,
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${totalText} ${
      programs?.length || 0
    } ${resultText}</div>`,
  };

  const handleRowInitSelected = (row: Tabulator.RowComponent) => {
    const rowData = row.getData();
    const groupCode = rowData.group_code;
    const groupName = rowData.group_name;

    const updateGroupData = () => {
      setGroupName(groupName);
      setSelectedCode({ group_code: groupCode });
      setIsRowClicked(true);
      setButtonState((prevState) => ({
        ...prevState,
        arrow: false,
        confirm: true,
        restore: true,
      }));
      setSelectedRowAssign([]);
      setSelectedRowStandby([]);
    };

    if (hasChangedRef.current) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });

      message.confirmClicked().then(() => {
        updateGroupData();
        setHasChanged(false);
      });
    } else {
      updateGroupData();
    }
  };

  const handleRowSelectedAssign = useCallback((row: Program[]) => {
    setSelectedRowAssign(row);
  }, []);

  const handleRowSelectedStandBy = useCallback(
    (row: Program[]) => {
      if (rowClickedRef.current) {
        setSelectedRowStandby(row);
      } else {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('osmsp13 > group not selected'));
        tbRefStandby.current?.deselectRow();
        return;
      }
    },
    [t]
  );

  const handleOnChangeInputSelect = useCallback(({ target }: { target: { value: string } }) => {
    const { value } = target;
    setSelectedIsCodeGroup(value);
  }, []);

  const handleSearchInit = useCallback(
    (inputVal: string | null = null) => {
      const params: GroupParams = {};

      if (inputVal) {
        params.input = inputVal;
      }

      if (selectedIsCodeGroup) {
        params.is_used = selectedIsCodeGroup;
      }

      setQueryParams(params);
    },
    [selectedIsCodeGroup]
  );

  const handleSearchStandby = useCallback(
    (inputVal: string | null = null) => {
      const params: ProgramParams = {
        usage: '002001',
        deletion: '001002',
      };

      if (inputVal) {
        params.input = inputVal;
      }

      if (selectedUpperProgram) {
        params.upper_program = selectedUpperProgram;
      }

      setQueryParamsProgram(params);
    },
    [selectedUpperProgram]
  );

  const handleResetInit = () => {
    setSelectedIsCodeGroup('');
  };

  const handleOnChangeInputSelectProgram = useCallback(
    ({ target }: { target: { value: string } }) => {
      const { value } = target;
      setSelectedUpperProgram(value);
    },
    []
  );

  const handleResetStandby = () => {
    setSelectedUpperProgram('');
  };

  const optionsRadioFilterUsage = commonListData?.['002']
    ? [
        { value: '', label: t('cmn > all'), code: 'All' },
        ...(commonListData['002']?.code?.map((code, index) => ({
          value: code ?? '',
          label: commonListData['002']?.name[index] ?? '',
          code: code ?? '',
        })) || []),
      ]
    : [];

  useEffect(() => {
    if (programGroupsData) {
      setProgramsAssign(programGroupsData);
      setInitialProgramsAssign(programGroupsData);
    }
  }, [programGroupsData]);

  useEffect(() => {
    if (programsData) {
      setPrograms(programsData);
    }
  }, [programsData]);

  useEffect(() => {
    if (Array.isArray(programsData) && Array.isArray(programsAssign)) {
      const filteredPrograms = programsData.filter(
        (program) => !programsAssign.some((assigned) => assigned.id === program.id)
      );

      setPrograms((prevPrograms) => {
        const isSame =
          prevPrograms.length === filteredPrograms.length &&
          prevPrograms.every((p, i) => p.id === filteredPrograms[i]?.id);

        return isSame ? prevPrograms : filteredPrograms;
      });
    }
  }, [programsData, programsAssign]);

  const handleLeftButton = () => {
    if (selectedRowAssign && selectedRowAssign.length > 0) {
      setHasChanged(true);
      setButtonState((prevState) => ({
        ...prevState,
        confirm: false,
        restore: false,
        arrow: false,
      }));

      const selectedIds = selectedRowAssign.map((program) => program.id);

      setProgramsAssign((prev) => {
        const updatedAssign = prev.filter((program) => !selectedIds.includes(program.id));
        return updatedAssign;
      });

      setPrograms((prev) => {
        const existingIds = new Set(prev.map((program) => program.id));
        const newPrograms = (
          Array.isArray(selectedRowAssign) ? selectedRowAssign : [selectedRowAssign]
        ).filter((program) => !existingIds.has(program.id));

        const updatedPrograms = [...prev, ...newPrograms];
        return updatedPrograms;
      });
      setSelectedRowStandby((prev) => {
        const prevStandby = Array.isArray(prev) ? prev : [];
        return [...prevStandby, ...selectedRowAssign];
      });

      setSelectedRowAssign([]);
    } else {
      setHasChanged(false);
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > data not selected'));
    }
  };

  const handleRightButton = () => {
    if (selectedRowStandby && selectedRowStandby.length > 0) {
      setHasChanged(true);
      setButtonState((prevState) => ({
        ...prevState,
        confirm: false,
        restore: false,
        arrow: false,
      }));
      const selectedIds = selectedRowStandby.map((program) => program.id);

      setPrograms((prev) => {
        const updatedPrograms = prev.filter((program) => !selectedIds.includes(program.id));
        return updatedPrograms;
      });

      setProgramsAssign((prev) => {
        const existingIds = new Set(prev.map((program) => program.id));
        const newProgramsAssign = (
          Array.isArray(selectedRowStandby) ? selectedRowStandby : [selectedRowStandby]
        ).filter((program) => !existingIds.has(program.id));
        const updatedAssign = [...prev, ...newProgramsAssign];
        return updatedAssign;
      });

      setSelectedRowAssign((prev) => {
        const prevAssign = Array.isArray(prev) ? prev : [];
        return [...prevAssign, ...selectedRowStandby];
      });

      setSelectedRowStandby([]);
    } else {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > data not selected'));
    }
  };

  const handleDblStandbyClick = (_e: UIEvent, row: Tabulator.RowComponent) => {
    if (row) {
      const rowData = row.getData();
      if (rowClickedRef.current) {
        setPrograms((prevPrograms) => prevPrograms.filter((program) => program.id !== rowData.id));
        setProgramsAssign((prevProgramsAssign) => {
          return [...(prevProgramsAssign || []), rowData];
        });
        setSelectedRowAssign([rowData]);
        setButtonState((prevState) => ({
          ...prevState,
          confirm: false,
          restore: false,
          arrow: false,
        }));
      } else {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('osmsp13 > group not selected'));
      }
    }
  };

  const handleDblAssignClick = (_e: UIEvent, row: Tabulator.RowComponent) => {
    if (row) {
      const rowData = row.getData();
      setProgramsAssign((prevProgramsAssign) =>
        prevProgramsAssign.filter((program) => program.id !== rowData.id)
      );

      setPrograms((prevPrograms) => {
        return [...(prevPrograms || []), rowData];
      });
      setSelectedRowStandby([rowData]);
      setButtonState((prevState) => ({
        ...prevState,
        confirm: false,
        restore: false,
        arrow: false,
      }));
    }
  };

  const handleRestore = () => {
    setProgramsAssign(initialProgramsAssign);
    setHasChanged(false);
    setButtonState((prevState) => ({
      ...prevState,
      confirm: true,
      restore: true,
      arrow: false,
    }));
  };

  const handleConfirm = () => {
    const ids = programsAssign.map((program) => program.id);
    const dataToSend = {
      id: ids,
    };
    if (selectedCode?.group_code != null) {
      createGroupMutation.mutateAsync({
        id: selectedCode?.group_code,
        data: dataToSend,
      });
    }
  };

  return (
    <>
      <section className="wrap">
        <div className="header-title">
          <h3>{t('SYSTEM')}</h3>
          <h3>&gt;</h3>
          <h3>{t('SYSTEM-AUTHORITY')}</h3>
        </div>

        <ContainerCard>
          <Filtering
            placeholder={`${t('authority > name')} / ${t('cmn > description')}`}
            onSearch={handleSearchInit}
            onReset={handleResetInit}
            labelSelect={t('002')}
            disableFiltering={true}
            searchRef={searchRef}
          >
            <Select
              value={selectedIsCodeGroup}
              onChange={handleOnChangeInputSelect}
              options={optionsRadioFilterUsage}
              className={'custom-select'}
            />
          </Filtering>
        </ContainerCard>
      </section>
      <ContainerCard>
        <ReactTabulator
          className="container-tabullator"
          data={groupsData || []}
          columns={columnInit as unknown as ColumnDefinition[]}
          layout={'fitDataStretch'}
          options={optionsInit}
          onRef={(r) => {
            tbRefInit.current = r.current;
          }}
          events={{
            rowSelected: handleRowInitSelected,
            tableBuilt: () => {
              if (selectedCode?.group_code) {
                const row = tbRefInit.current?.getRow(selectedCode?.group_code) || null;
                row?.select();
              }
            },
          }}
        />
      </ContainerCard>
      <div className="flex gap-4 items-center ">
        <div className="flex-1 min-w-0">
          <ContainerCard>
            <div className="mb-5">
              <div className="flex gap-4 justify-between">
                <div className="w-32 flex items-center gap-2">
                  <span className="text-body-2 text-au-neutral-4 py-[9px] flex-grow whitespace-nowrap">
                    {groupName || t('cmn > team')}
                  </span>
                </div>
                <div className="flex gap-4 ml-auto">
                  <Button
                    label={t('cmn > confirm')}
                    customButton="btn-search"
                    disabled={buttonState.confirm}
                    onClick={() => handleConfirm?.()}
                  />
                  <Button
                    label={t('cmn > reset table')}
                    customButton="btn-reset"
                    disabled={buttonState.restore}
                    onClick={() => handleRestore?.()}
                  />
                </div>
              </div>
            </div>
            <ReactTabulator
              key={programsAssign?.length}
              className="container-tabullator"
              data={programsAssign}
              columns={columnAssign as unknown as ColumnDefinition[]}
              layout={'fitDataStretch'}
              options={optionsAssign}
              onRef={(r) => {
                tbRefAssign.current = r.current;
              }}
              events={{
                rowSelectionChanged: handleRowSelectedAssign,
                rowDblClick: handleDblAssignClick,
                tableBuilt: () => {
                  if (selectedCode?.group_code) {
                    tbRefAssign.current?.selectRow(selectedRowAssign.map((p) => p.id));
                  }
                },
              }}
            />
          </ContainerCard>
        </div>
        <div className="flex-shrink-0 min-w-0">
          <div className="flex flex-col items-center space-y-4">
            <button
              type="button"
              className="_slideLeft"
              id="exclusion"
              disabled={buttonState.arrow}
              onClick={handleLeftButton}
            >
              <img src={arrowLeft} alt="Arrow Left" />
            </button>
            <button
              type="button"
              className="_slideRight"
              id="allocation"
              disabled={buttonState.arrow}
              onClick={handleRightButton}
            >
              <img src={arrowRight} alt="Arrow Right" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <ContainerCard>
            <div className="mb-5">
              <Filtering
                placeholder={t('cmn > search')}
                onSearch={handleSearchStandby}
                onReset={handleResetStandby}
                labelSelect={t('program > group program')}
                disableFiltering={true}
                customWidthInput={'flex-1 basis-[200px]'}
                customWidthSelect={'flex-1 basis-[200px]'}
              >
                <Select
                  className={'custom-select'}
                  value={selectedUpperProgram}
                  onChange={handleOnChangeInputSelectProgram}
                  options={[
                    { label: t('cmn > all'), value: '' },
                    ...(upperProgramsData
                      ? upperProgramsData.map((item) => ({
                          label: item.upper,
                          value: item.upper,
                        }))
                      : []),
                  ]}
                />
              </Filtering>
            </div>
            <ReactTabulator
              key={programs?.length}
              className="container-tabullator"
              data={programs}
              columns={columnStandby as unknown as ColumnDefinition[]}
              layout={'fitDataStretch'}
              options={optionsStandby}
              onRef={(r) => {
                tbRefStandby.current = r.current;
              }}
              events={{
                rowSelectionChanged: handleRowSelectedStandBy,
                rowDblClick: handleDblStandbyClick,
                tableBuilt: () => {
                  if (selectedCode?.group_code) {
                    tbRefStandby.current?.selectRow(selectedRowStandby.map((p) => p.id));
                  }
                },
              }}
            />
          </ContainerCard>
        </div>
      </div>
    </>
  );
};

export default AuthorityManagement;
